import React from "react";
import { Search, X, CalendarDays } from "lucide-react";

const TaskFilters = ({
  role,
  searchQuery,
  setSearchQuery,
  departments,
  selectedDepartment,
  selectedEmployee,
  departmentFilterDate,
  setDepartmentFilterDate,
  users,
  handleEmployeeSelect,
  handleDepartmentSelect,
  handleClearSelection,
}) => {
  // Hide filters for regular employees
  if (role === "employee") {
    return <div className="bg-white border-b border-gray-200"></div>;
  }

  // Filter users based on search query
  const filteredUsers = searchQuery
    ? users.filter((user) =>
        `${user.firstName} ${user.lastName}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      )
    : [];

  return (
    <div className="bg-white border-b border-gray-200 p-4 space-y-4">
      {/* Search */}
      <div className="relative">
        <Search
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          size={20}
        />
        <input
          type="text"
          placeholder={
            role === "manager" 
              ? "Search employees in your department..." 
              : "Search employees..."
          }
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      {/* Employee List - Only show when searchQuery exists */}
      {searchQuery && filteredUsers.length > 0 && (
        <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
          {filteredUsers.map((user) => (
            <button
              key={user._id}
              onClick={() => handleEmployeeSelect(user)}
              className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition flex justify-between items-center ${
                selectedEmployee?._id === user._id
                  ? "bg-indigo-50 border-l-4 border-indigo-500"
                  : ""
              }`}
            >
              <div>
                <div className="font-medium">
                  {user.firstName} {user.lastName}
                </div>
                <div className="text-sm text-gray-600">
                  {user.position} • {user.department}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results - Only show when searchQuery exists but no matches */}
      {searchQuery && filteredUsers.length === 0 && (
        <div className="text-center py-4 text-gray-500">
          No employees found matching "{searchQuery}"
        </div>
      )}

      {/* Department Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleClearSelection}
          className={`px-3 py-1 rounded-full text-sm font-medium transition ${
            !selectedDepartment && !selectedEmployee
              ? "bg-indigo-100 text-indigo-700 border-indigo-300"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          } border`}
          disabled={role === "manager"} // Managers always have their department selected
        >
          {role === "manager" ? "My Department" : "All"}
        </button>
        {departments.map((dept) => (
          <button
            key={dept}
            onClick={() => handleDepartmentSelect(dept)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition ${
              selectedDepartment === dept
                ? "bg-indigo-100 text-indigo-700 border-indigo-300"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            } border ${role === "manager" ? "cursor-default" : ""}`}
            disabled={role === "manager"} // Managers can't switch departments
          >
            {dept}
          </button>
        ))}
      </div>

      {/* Department Date Filter */}
      {selectedDepartment && (
        <div className="flex items-center gap-3 bg-indigo-50 p-3 rounded-lg border border-indigo-200">
          <CalendarDays className="text-indigo-600" size={20} />
          <div className="flex-1">
            <input
              type="date"
              value={departmentFilterDate}
              onChange={(e) => setDepartmentFilterDate(e.target.value)}
              className="max-w-2xl px-3 py-1.5 border border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
            />
          </div>
          {departmentFilterDate && (
            <button
              onClick={() => setDepartmentFilterDate("")}
              className="p-1.5 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100 rounded-lg transition"
              title="Clear date filter"
            >
              <X size={18} />
            </button>
          )}
        </div>
      )}
      
      {/* Manager info badge */}
      {role === "manager" && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            <span className="font-semibold">Manager View:</span> You can only view and manage tasks for employees in your department.
          </p>
        </div>
      )}
    </div>
  );
};

export default TaskFilters;